import React, { useState, useCallback, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { add, format, parseISO } from "date-fns";

import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Button,
  TableBody,
  TableRow,
  TableCell,
  Table,
  TableHead,
  Tooltip,
  Typography,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem as MuiMenuItem,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import {
  LuPencil,
  LuTrash2,
  LuRefreshCw,
  LuLink,
  LuWifi,
  LuWifiOff,
  LuScanLine,
  LuCheckCircle2,
} from "react-icons/lu";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";

import MainContainer from "../../components/MainContainer";
import {
  CrudListPage,
  CrudActionButton,
  useCrudListStyles,
  useModernDialogStyles,
} from "../../components/CrudListPage";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import formatSerializedId from "../../utils/formatSerializedId";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";
import { Can } from "../../components/Can";

const useLocalStyles = makeStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    fontSize: theme.typography.pxToRem(14),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    maxWidth: 450,
    boxShadow:
      theme.palette.type === "light"
        ? "0 4px 12px rgba(16, 24, 40, 0.1)"
        : "0 4px 12px rgba(0, 0, 0, 0.3)",
  },
  tooltipPopper: {
    textAlign: "center",
  },
  tooltipTrigger: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  buttonProgress: {
    color: green[500],
  },
  colorSwatch: {
    width: 60,
    height: 20,
    borderRadius: 4,
    display: "inline-block",
  },
  colorCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statusCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statusConnected: {
    color: theme.palette.success.main,
  },
  sessionActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    justifyContent: "center",
    alignItems: "center",
  },
  sessionButton: {
    borderRadius: 8,
    whiteSpace: "nowrap",
  },
  importCard: {
    margin: theme.spacing(2),
    marginBottom: 0,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  importCardContent: {
    padding: theme.spacing(2, 2.5),
    "&:last-child": {
      paddingBottom: theme.spacing(2),
    },
  },
  defaultIcon: {
    color: theme.palette.success.main,
  },
  channelMenuIcon: {
    marginRight: theme.spacing(1.25),
  },
}));

function CircularProgressWithLabel(props) {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress variant="determinate" {...props} />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography
          variant="caption"
          component="div"
          color="textSecondary"
        >{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

const CustomToolTip = ({ title, content, children }) => {
  const classes = useLocalStyles();

  return (
    <Tooltip
      arrow
      classes={{
        tooltip: classes.tooltip,
        popper: classes.tooltipPopper,
      }}
      title={
        <React.Fragment>
          <Typography gutterBottom color="inherit">
            {title}
          </Typography>
          {content && <Typography>{content}</Typography>}
        </React.Fragment>
      }
    >
      <span className={classes.tooltipTrigger}>{children}</span>
    </Tooltip>
  );
};

const IconChannel = (channel) => {
  switch (channel) {
    case "facebook":
      return <Facebook style={{ color: "#3b5998" }} />;
    case "instagram":
      return <Instagram style={{ color: "#e1306c" }} />;
    case "whatsapp":
      return <WhatsApp style={{ color: "#25d366" }} />;
    case "whatsapp_oficial":
      return <WhatsApp style={{ color: "#25d366" }} />;
    default:
      return "error";
  }
};

const Connections = () => {
  const classes = useCrudListStyles({ tableMinWidth: 1100 });
  const localClasses = useLocalStyles();
  const dialogClasses = useModernDialogStyles();
  const theme = useTheme();

  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [statusImport, setStatusImport] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [channel, setChannel] = useState("whatsapp");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const history = useHistory();
  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(
    confirmationModalInitialState
  );
  const [planConfig, setPlanConfig] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [sourceConnection, setSourceConnection] = useState("");
  const [targetConnection, setTargetConnection] = useState("");
  const [preDeleteModalOpen, setPreDeleteModalOpen] = useState(false);
  const [whatsAppToDelete, setWhatsAppToDelete] = useState(null);
  const [transferProgressModalOpen, setTransferProgressModalOpen] =
    useState(false);
  const [transferProgress, setTransferProgress] = useState({
    current: 0,
    total: 0,
    percentage: 0,
  });

  const { user, socket } = useContext(AuthContext);
  const companyId = user?.companyId;
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      setPlanConfig(planConfigs);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const responseFacebook = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then(() => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };

  const responseInstagram = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          addInstagram: true,
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then(() => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };

  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const importEvent = `importMessages-${user.companyId}`;
    const transferEvent = `transferTickets-${user.companyId}`;

    const onImportMessages = (data) => {
      if (data.action === "refresh") {
        setStatusImport([]);
        history.go(0);
      }
      if (data.action === "update") {
        setStatusImport(data.status);
      }
    };

    const onTransferTickets = (data) => {
      if (data.action === "progress") {
        setTransferProgress({
          current: data.current,
          total: data.total,
          percentage: Math.round((data.current / data.total) * 100),
        });
      }
      if (data.action === "completed") {
        setTransferProgressModalOpen(false);
        setTransferProgress({ current: 0, total: 0, percentage: 0 });
        toast.success(
          `Transferência concluída! ${data.transferred} tickets transferidos com sucesso.`
        );
        setTransferModalOpen(false);
        setSourceConnection("");
        setTargetConnection("");
      }
      if (data.action === "error") {
        setTransferProgressModalOpen(false);
        setTransferProgress({ current: 0, total: 0, percentage: 0 });
        toast.error("Erro na transferência de tickets.");
      }
    };

    socket.on(importEvent, onImportMessages);
    socket.on(transferEvent, onTransferTickets);

    return () => {
      socket.off(importEvent, onImportMessages);
      socket.off(transferEvent, onTransferTickets);
    };
  }, [socket, user?.companyId, history]);

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = (nextChannel) => {
    setChannel(nextChannel || "whatsapp");
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
  }, [setSelectedWhatsApp, setWhatsAppModalOpen]);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, [setQrModalOpen, setSelectedWhatsApp]);

  const handleEditWhatsApp = (whatsApp) => {
    setChannel(whatsApp.channel);
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const handleSyncTemplates = async (whatsAppId) => {
    await api.get(`/whatsapp/sync-templates/${whatsAppId}`);
  };

  const handleCopyWebhook = (url) => {
    navigator.clipboard.writeText(url);
  };

  const openInNewTab = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId: whatsAppId,
      });
    }

    if (action === "delete") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId: whatsAppId,
      });
    }
    if (action === "closedImported") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.closedImportedTitle"),
        message: i18n.t("connections.confirmationModal.closedImportedMessage"),
        whatsAppId: whatsAppId,
      });
    }

    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      } catch (err) {
        toastError(err);
      }
    }

    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "closedImported") {
      try {
        await api.post(`/closedimported/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.closedimported"));
      } catch (err) {
        toastError(err);
      }
    }

    setConfirmModalInfo(confirmationModalInitialState);
  };

  const renderImportButton = (whatsApp) => {
    if (whatsApp?.statusImportMessages === "renderButtonCloseTickets") {
      return (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          className={localClasses.sessionButton}
          onClick={() => {
            handleOpenConfirmationModal("closedImported", whatsApp.id);
          }}
        >
          {i18n.t("connections.buttons.closedImported")}
        </Button>
      );
    }

    if (whatsApp?.importOldMessages) {
      const isTimeStamp = !isNaN(
        new Date(Math.floor(whatsApp?.statusImportMessages)).getTime()
      );

      if (isTimeStamp) {
        const ultimoStatus = new Date(
          Math.floor(whatsApp?.statusImportMessages)
        ).getTime();
        const dataLimite = +add(ultimoStatus, { seconds: +35 }).getTime();
        if (dataLimite > new Date().getTime()) {
          return (
            <Button
              disabled
              size="small"
              endIcon={
                <CircularProgress
                  size={12}
                  className={localClasses.buttonProgress}
                />
              }
              variant="outlined"
              color="primary"
              className={localClasses.sessionButton}
            >
              {i18n.t("connections.buttons.preparing")}
            </Button>
          );
        }
      }
    }
  };

  const renderActionButtons = (whatsApp) => {
    return (
      <div className={localClasses.sessionActions}>
        {whatsApp.channel === "whatsapp" && whatsApp.status === "qrcode" && (
          <Can
            role={
              user.profile === "user" && user.allowConnections === "enabled"
                ? "admin"
                : user.profile
            }
            perform="connections-page:addConnection"
            yes={() => (
              <Button
                size="small"
                variant="contained"
                color="primary"
                className={localClasses.sessionButton}
                onClick={() => handleOpenQrModal(whatsApp)}
              >
                {i18n.t("connections.buttons.qrcode")}
              </Button>
            )}
          />
        )}
        {whatsApp.channel === "whatsapp" &&
          whatsApp.status === "DISCONNECTED" && (
            <Can
              role={
                user.profile === "user" && user.allowConnections === "enabled"
                  ? "admin"
                  : user.profile
              }
              perform="connections-page:addConnection"
              yes={() => (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    className={localClasses.sessionButton}
                    onClick={() => handleStartWhatsAppSession(whatsApp.id)}
                  >
                    {i18n.t("connections.buttons.tryAgain")}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    className={localClasses.sessionButton}
                    onClick={() => handleRequestNewQrCode(whatsApp.id)}
                  >
                    {i18n.t("connections.buttons.newQr")}
                  </Button>
                </>
              )}
            />
          )}
        {(whatsApp.channel === "whatsapp" &&
          (whatsApp.status === "CONNECTED" ||
            whatsApp.status === "PAIRING" ||
            whatsApp.status === "TIMEOUT")) && (
          <Can
            role={user.profile}
            perform="connections-page:addConnection"
            yes={() => (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  className={localClasses.sessionButton}
                  onClick={() => {
                    handleOpenConfirmationModal("disconnect", whatsApp.id);
                  }}
                >
                  {i18n.t("connections.buttons.disconnect")}
                </Button>
                {renderImportButton(whatsApp)}
              </>
            )}
          />
        )}
        {whatsApp.channel === "whatsapp" && whatsApp.status === "OPENING" && (
          <Button
            size="small"
            variant="outlined"
            disabled
            color="default"
            className={localClasses.sessionButton}
          >
            {i18n.t("connections.buttons.connecting")}
          </Button>
        )}
      </div>
    );
  };

  const renderStatusToolTips = (whatsApp) => {
    const iconProps = { size: 20, strokeWidth: 1.75, "aria-hidden": true };

    return (
      <div className={localClasses.statusCell}>
        {whatsApp.status === "DISCONNECTED" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.disconnected.title")}
            content={i18n.t("connections.toolTips.disconnected.content")}
          >
            <LuWifiOff {...iconProps} color={theme.palette.error.main} />
          </CustomToolTip>
        )}
        {whatsApp.status === "OPENING" && (
          <CircularProgress size={24} className={localClasses.buttonProgress} />
        )}
        {whatsApp.status === "qrcode" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.qrcode.title")}
            content={i18n.t("connections.toolTips.qrcode.content")}
          >
            <LuScanLine {...iconProps} />
          </CustomToolTip>
        )}
        {whatsApp.status === "CONNECTED" && (
          <CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
            <LuWifi
              {...iconProps}
              className={localClasses.statusConnected}
            />
          </CustomToolTip>
        )}
        {(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.timeout.title")}
            content={i18n.t("connections.toolTips.timeout.content")}
          >
            <LuWifiOff {...iconProps} color={theme.palette.warning.main} />
          </CustomToolTip>
        )}
      </div>
    );
  };

  const restartWhatsapps = async () => {
    try {
      await api.post(`/whatsapp-restart/`);
      toast.success(i18n.t("connections.waitConnection"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenTransferModal = () => {
    setTransferModalOpen(true);
  };

  const handleCloseTransferModal = () => {
    setTransferModalOpen(false);
    setSourceConnection("");
    setTargetConnection("");
  };

  const handleCloseTransferProgressModal = () => {
    setTransferProgressModalOpen(false);
    setTransferProgress({ current: 0, total: 0, percentage: 0 });
  };

  const handleTransferTickets = async () => {
    if (!sourceConnection || !targetConnection) {
      toast.error("Selecione as conexões de origem e destino");
      return;
    }

    if (sourceConnection === targetConnection) {
      toast.error("As conexões de origem e destino devem ser diferentes");
      return;
    }

    try {
      const response = await api.post(`/transfer-tickets`, {
        sourceConnectionId: sourceConnection,
        targetConnectionId: targetConnection,
      });

      if (response.data.requiresProgress) {
        setTransferModalOpen(false);
        setTransferProgressModalOpen(true);
        setTransferProgress({
          current: 0,
          total: response.data.totalTickets,
          percentage: 0,
        });
      } else {
        toast.success(
          `Tickets transferidos com sucesso! ${response.data.transferred || 0} tickets transferidos.`
        );
        handleCloseTransferModal();
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenPreDeleteModal = (whatsAppId) => {
    setWhatsAppToDelete(whatsAppId);
    setPreDeleteModalOpen(true);
  };

  const handleClosePreDeleteModal = () => {
    setPreDeleteModalOpen(false);
    setWhatsAppToDelete(null);
  };

  const handleConfirmTransferDone = () => {
    setPreDeleteModalOpen(false);
    handleOpenConfirmationModal("delete", whatsAppToDelete);
    setWhatsAppToDelete(null);
  };

  const formatUpdatedAt = (updatedAt) => {
    if (!updatedAt) return "-";
    try {
      return format(parseISO(updatedAt), "dd/MM/yy HH:mm");
    } catch (err) {
      return "-";
    }
  };

  const modals = (
    <>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      {qrModalOpen && (
        <QrcodeModal
          open={qrModalOpen}
          onClose={handleCloseQrModal}
          whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
        />
      )}
      <WhatsAppModal
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
        channel={channel}
      />
      <Dialog
        open={transferModalOpen}
        onClose={handleCloseTransferModal}
        maxWidth="sm"
        fullWidth
        classes={{ paper: dialogClasses.paper }}
      >
        <DialogTitle className={dialogClasses.title}>
          Transferência de Tickets
        </DialogTitle>
        <DialogContent className={dialogClasses.content}>
          <Typography className={dialogClasses.bodyText} paragraph>
            Para transferir os tickets, selecione a conexão de{" "}
            <strong>origem</strong> (de onde os tickets serão movidos) e a
            conexão de <strong>destino</strong> (para onde os tickets serão
            transferidos). Todos os atendimentos ativos da conexão de origem
            serão movidos para a conexão de destino.
          </Typography>

          <div className={dialogClasses.formRow}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Origem</InputLabel>
              <Select
                value={sourceConnection}
                onChange={(e) => setSourceConnection(e.target.value)}
                label="Origem"
              >
                {whatsApps.map((whatsApp) => (
                  <MuiMenuItem key={whatsApp.id} value={whatsApp.id}>
                    {whatsApp.name}
                  </MuiMenuItem>
                ))}
              </Select>
            </FormControl>

            <div className={dialogClasses.arrowDivider}>→</div>

            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Destino</InputLabel>
              <Select
                value={targetConnection}
                onChange={(e) => setTargetConnection(e.target.value)}
                label="Destino"
              >
                {whatsApps.map((whatsApp) => (
                  <MuiMenuItem key={whatsApp.id} value={whatsApp.id}>
                    {whatsApp.name}
                  </MuiMenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions className={dialogClasses.actions}>
          <Button
            onClick={handleCloseTransferModal}
            color="default"
            variant="outlined"
            className={dialogClasses.cancelButton}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleTransferTickets}
            color="primary"
            variant="contained"
            className={dialogClasses.confirmButton}
          >
            Transferir
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={transferProgressModalOpen}
        onClose={handleCloseTransferProgressModal}
        maxWidth="sm"
        fullWidth
        disableBackdropClick
        disableEscapeKeyDown
        classes={{ paper: dialogClasses.paper }}
      >
        <DialogTitle className={dialogClasses.title}>
          Transferindo Tickets
        </DialogTitle>
        <DialogContent className={dialogClasses.content}>
          <div className={dialogClasses.progressWrap}>
            <Typography variant="h6" gutterBottom>
              Progresso da Transferência
            </Typography>

            <Box
              position="relative"
              display="inline-flex"
              className={dialogClasses.progressRing}
            >
              <CircularProgress
                variant="determinate"
                value={transferProgress.percentage}
                size={80}
                thickness={4}
              />
              <Box
                top={0}
                left={0}
                bottom={0}
                right={0}
                position="absolute"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography
                  variant="caption"
                  component="div"
                  color="textSecondary"
                  style={{ fontSize: "14px", fontWeight: "bold" }}
                >
                  {transferProgress.percentage}%
                </Typography>
              </Box>
            </Box>

            <Typography variant="body1">
              {transferProgress.current} de {transferProgress.total} tickets
              transferidos
            </Typography>

            <Typography variant="body2" color="textSecondary">
              Por favor, aguarde enquanto os tickets são transferidos...
            </Typography>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={preDeleteModalOpen}
        onClose={handleClosePreDeleteModal}
        maxWidth="sm"
        fullWidth
        classes={{ paper: dialogClasses.paper }}
      >
        <DialogTitle className={dialogClasses.title}>
          Transferência de Tickets
        </DialogTitle>
        <DialogContent className={dialogClasses.content}>
          <Typography className={dialogClasses.bodyText}>
            Antes de excluir esta conexão, você já fez a transferência dos
            tickets para outra conexão?
          </Typography>
        </DialogContent>
        <DialogActions className={dialogClasses.actions}>
          <Button
            onClick={handleClosePreDeleteModal}
            color="default"
            variant="outlined"
            className={dialogClasses.cancelButton}
          >
            Não
          </Button>
          <Button
            onClick={handleConfirmTransferDone}
            color="primary"
            variant="contained"
            className={dialogClasses.confirmButton}
          >
            Sim
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return (
    <MainContainer>
      {modals}
      {!user ? null : user.profile === "user" &&
        user.allowConnections === "disabled" ? (
        <ForbiddenPage />
      ) : (
        <CrudListPage
          title={`${i18n.t("connections.title")} (${whatsApps?.length ?? 0})`}
          tableMinWidth={1100}
          toolbarWide
          toolbar={
            <>
              <Button
                variant="outlined"
                color="primary"
                className={classes.secondaryButton}
                onClick={handleOpenTransferModal}
              >
                Transferir Tickets
              </Button>
              <Button
                variant="outlined"
                color="primary"
                className={classes.secondaryButton}
                onClick={restartWhatsapps}
              >
                {i18n.t("connections.restartConnections")}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                className={classes.secondaryButton}
                onClick={() =>
                  openInNewTab(
                    `https://wa.me/${process.env.REACT_APP_NUMBER_SUPPORT}`
                  )
                }
              >
                {i18n.t("connections.callSupport")}
              </Button>
              <PopupState variant="popover" popupId="demo-popup-menu">
                {(popupState) => (
                  <React.Fragment>
                    <Can
                      role={user.profile}
                      perform="connections-page:addConnection"
                      yes={() => (
                        <>
                          <Button
                            variant="contained"
                            color="primary"
                            className={classes.primaryButton}
                            {...bindTrigger(popupState)}
                          >
                            {i18n.t("connections.newConnection")}
                          </Button>
                          <Menu {...bindMenu(popupState)}>
                            <MenuItem
                              disabled={
                                planConfig?.plan?.useWhatsapp ? false : true
                              }
                              onClick={() => {
                                handleOpenWhatsAppModal("whatsapp");
                                popupState.close();
                              }}
                            >
                              <WhatsApp
                                fontSize="small"
                                className={localClasses.channelMenuIcon}
                                style={{ color: "#25D366" }}
                              />
                              WhatsApp
                            </MenuItem>
                            <MenuItem
                              disabled={
                                planConfig?.plan?.useWhatsappOfficial
                                  ? false
                                  : true
                              }
                              onClick={() => {
                                handleOpenWhatsAppModal("whatsapp_oficial");
                                popupState.close();
                              }}
                            >
                              <WhatsApp
                                fontSize="small"
                                className={localClasses.channelMenuIcon}
                                style={{ color: "#25D366" }}
                              />
                              WhatsApp Oficial
                            </MenuItem>
                            <FacebookLogin
                              appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                              autoLoad={false}
                              fields="name,email,picture"
                              version="9.0"
                              scope={
                                process.env.REACT_APP_REQUIRE_BUSINESS_MANAGEMENT?.toUpperCase() ===
                                "TRUE"
                                  ? "public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                                  : "public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement"
                              }
                              callback={responseFacebook}
                              render={(renderProps) => (
                                <MenuItem
                                  disabled={
                                    planConfig?.plan?.useFacebook ? false : true
                                  }
                                  onClick={renderProps.onClick}
                                >
                                  <Facebook
                                    fontSize="small"
                                    className={localClasses.channelMenuIcon}
                                    style={{ color: "#3b5998" }}
                                  />
                                  Facebook
                                </MenuItem>
                              )}
                            />
                            <FacebookLogin
                              appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                              autoLoad={false}
                              fields="name,email,picture"
                              version="9.0"
                              scope={
                                process.env.REACT_APP_REQUIRE_BUSINESS_MANAGEMENT?.toUpperCase() ===
                                "TRUE"
                                  ? "public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                                  : "public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement"
                              }
                              callback={responseInstagram}
                              render={(renderProps) => (
                                <MenuItem
                                  disabled={
                                    planConfig?.plan?.useInstagram ? false : true
                                  }
                                  onClick={renderProps.onClick}
                                >
                                  <Instagram
                                    fontSize="small"
                                    className={localClasses.channelMenuIcon}
                                    style={{ color: "#e1306c" }}
                                  />
                                  Instagram
                                </MenuItem>
                              )}
                            />
                          </Menu>
                        </>
                      )}
                    />
                  </React.Fragment>
                )}
              </PopupState>
            </>
          }
        >
          {statusImport?.all ? (
            <Card className={localClasses.importCard} variant="outlined">
              <CardContent className={localClasses.importCardContent}>
                <Typography component="h5" variant="h5">
                  {statusImport?.this === -1
                    ? i18n.t("connections.buttons.preparing")
                    : i18n.t("connections.buttons.importing")}
                </Typography>
                {statusImport?.this === -1 ? (
                  <Typography component="h6" variant="h6" align="center">
                    <CircularProgress size={24} />
                  </Typography>
                ) : (
                  <>
                    <Typography component="h6" variant="h6" align="center">
                      {`${i18n.t(`connections.typography.processed`)} ${statusImport?.this} ${i18n.t(`connections.typography.in`)} ${statusImport?.all}  ${i18n.t(`connections.typography.date`)}: ${statusImport?.date} `}
                    </Typography>
                    <Typography align="center">
                      <CircularProgressWithLabel
                        value={(statusImport?.this / statusImport?.all) * 100}
                      />
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          ) : null}

          <Table size="small" stickyHeader className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell align="center">Channel</TableCell>
                <TableCell align="center">
                  {i18n.t("connections.table.color")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("connections.table.name")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("connections.table.number")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("connections.table.status")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("connections.table.session")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("connections.table.lastUpdate")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("connections.table.default")}
                </TableCell>
                <Can
                  role={
                    user.profile === "user" &&
                    user.allowConnections === "enabled"
                      ? "admin"
                      : user.profile
                  }
                  perform="connections-page:addConnection"
                  yes={() => (
                    <TableCell align="center">
                      {i18n.t("connections.table.actions")}
                    </TableCell>
                  )}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRowSkeleton columns={9} />
              ) : (
                whatsApps?.length > 0 &&
                whatsApps.map((whatsApp) => (
                  <TableRow key={whatsApp.id}>
                    <TableCell align="center">
                      {IconChannel(whatsApp.channel)}
                    </TableCell>
                    <TableCell align="center">
                      <div className={localClasses.colorCell}>
                        <span
                          className={localClasses.colorSwatch}
                          style={{ backgroundColor: whatsApp.color }}
                        />
                      </div>
                    </TableCell>
                    <TableCell align="center">{whatsApp.name}</TableCell>
                    <TableCell align="center">
                      {whatsApp.channel === "whatsapp" && whatsApp.number ? (
                        <>{formatSerializedId(whatsApp.number)}</>
                      ) : whatsApp.channel === "whatsapp_oficial" &&
                        whatsApp.phone_number ? (
                        <>{formatSerializedId(whatsApp.phone_number)}</>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {renderStatusToolTips(whatsApp)}
                    </TableCell>
                    <TableCell align="center">
                      {renderActionButtons(whatsApp)}
                    </TableCell>
                    <TableCell align="center">
                      {formatUpdatedAt(whatsApp.updatedAt)}
                    </TableCell>
                    <TableCell align="center">
                      {whatsApp.isDefault && (
                        <LuCheckCircle2
                          size={20}
                          strokeWidth={1.75}
                          className={localClasses.defaultIcon}
                          aria-label="Conexão padrão"
                        />
                      )}
                    </TableCell>
                    <Can
                      role={user.profile}
                      perform="connections-page:addConnection"
                      yes={() => (
                        <TableCell
                          align="center"
                          className={classes.actionsCell}
                        >
                          <CrudActionButton
                            classes={classes}
                            icon={LuPencil}
                            onClick={() => handleEditWhatsApp(whatsApp)}
                            ariaLabel="Editar conexão"
                          />
                          <CrudActionButton
                            classes={classes}
                            icon={LuTrash2}
                            onClick={() => handleOpenPreDeleteModal(whatsApp.id)}
                            ariaLabel="Excluir conexão"
                            danger
                          />
                          {whatsApp.channel === "whatsapp_oficial" && (
                            <>
                              <CrudActionButton
                                classes={classes}
                                icon={LuRefreshCw}
                                onClick={() => handleSyncTemplates(whatsApp.id)}
                                ariaLabel="Sincronizar templates"
                              />
                              <CrudActionButton
                                classes={classes}
                                icon={LuLink}
                                onClick={() =>
                                  handleCopyWebhook(whatsApp.waba_webhook)
                                }
                                ariaLabel="Copiar webhook para Meta"
                              />
                            </>
                          )}
                        </TableCell>
                      )}
                    />
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CrudListPage>
      )}
    </MainContainer>
  );
};

export default Connections;
